import { FC, useRef, useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { DocumentIcon } from '@heroicons/react/24/outline';
import PageHeader from '@/components/ui/PageHeader/PageHeader';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import TextInput from '@/components/functional/TextInput/TextInput';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import Stepper from '@/components/shared/Stepper/Stepper';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext';
import { useUploadPastPaper, getPaperCode } from '@/api/useUploadPastPaper';
import Routes from '@/router/Routes';

const STEPS = ['details', 'upload', 'processing'] as const;

const SUBJECT_OPTIONS = [
  { value: 'english_language', label: 'English Language' },
  { value: 'english_literature', label: 'English Literature' },
] as const;

const EXAM_BOARD_OPTIONS = [{ value: 'edexcel', label: 'Edexcel' }] as const;

const PAPER_NUMBER_OPTIONS = [
  { value: 'Paper 1', label: 'Paper 1' },
  { value: 'Paper 2', label: 'Paper 2' },
] as const;

const MONTH_OPTIONS = [
  { value: 'january', label: 'January' },
  { value: 'february', label: 'February' },
  { value: 'march', label: 'March' },
  { value: 'april', label: 'April' },
  { value: 'may', label: 'May' },
  { value: 'june', label: 'June' },
  { value: 'july', label: 'July' },
  { value: 'august', label: 'August' },
  { value: 'september', label: 'September' },
  { value: 'october', label: 'October' },
  { value: 'november', label: 'November' },
  { value: 'december', label: 'December' },
] as const;

const currentYear = new Date().getFullYear();
const MIN_YEAR = 2015;

const detailsSchema = z.object({
  subject: z.enum(['english_language', 'english_literature']),
  examBoard: z.enum(['edexcel']),
  paperNumber: z.enum(['Paper 1', 'Paper 2']),
  month: z.string().min(1, 'Please select a month'),
  year: z
    .string()
    .min(1, 'Please enter a year')
    .refine((val) => /^\d{4}$/.test(val), 'Year must be a 4-digit number')
    .refine((val) => {
      const year = parseInt(val, 10);
      return year >= MIN_YEAR && year <= currentYear;
    }, `Year must be between ${MIN_YEAR} and ${currentYear}`),
});

type DetailsSchema = z.infer<typeof detailsSchema>;

const getPaperCodeDescription = (paperCode: string): string => {
  const descriptions: Record<string, string> = {
    '1EN0/01': 'English Language Paper 1',
    '1EN0/02': 'English Language Paper 2',
    '1ET0/01': 'English Literature Paper 1',
    '1ET0/02': 'English Literature Paper 2',
  };
  return descriptions[paperCode] || '';
};

type UploadFormData = {
  subject: 'english_language' | 'english_literature';
  examBoard: 'edexcel';
  paperNumber: 'Paper 1' | 'Paper 2';
  month: string;
  year: string;
  file?: File;
};

const DetailsStep: FC = () => {
  const { nextStep, setContextData, contextData } = useStepperContext<typeof STEPS, UploadFormData>();

  const { control, handleSubmit, watch, formState: { errors } } = useForm<DetailsSchema>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      subject: contextData?.subject ?? 'english_language',
      examBoard: contextData?.examBoard ?? 'edexcel',
      paperNumber: contextData?.paperNumber ?? 'Paper 1',
      month: contextData?.month ?? 'june',
      year: contextData?.year ?? String(currentYear),
    },
  });

  const subject = watch('subject');
  const paperNumber = watch('paperNumber');

  const paperCode = useMemo(() => getPaperCode(subject, paperNumber), [subject, paperNumber]);
  const paperDescription = useMemo(() => getPaperCodeDescription(paperCode), [paperCode]);

  const handleNext = (data: DetailsSchema) => {
    setContextData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
      <Controller
        control={control}
        name="subject"
        render={({ field: { onChange, value } }) => (
          <SelectInput
            label="Choose subject"
            value={value}
            onChange={onChange}
            options={[...SUBJECT_OPTIONS]}
            error={errors.subject?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="examBoard"
        render={({ field: { value } }) => (
          <SelectInput
            label="Choose exam board"
            value={value}
            onChange={() => {}}
            options={[...EXAM_BOARD_OPTIONS]}
            readOnly
          />
        )}
      />

      <Controller
        control={control}
        name="paperNumber"
        render={({ field: { onChange, value } }) => (
          <SelectInput
            label="Choose paper number (type)"
            value={value}
            onChange={onChange}
            options={[...PAPER_NUMBER_OPTIONS]}
            error={errors.paperNumber?.message}
          />
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <Controller
          control={control}
          name="month"
          render={({ field: { onChange, value } }) => (
            <SelectInput
              label="Month"
              value={value}
              onChange={onChange}
              options={[...MONTH_OPTIONS]}
              error={errors.month?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="year"
          render={({ field: { onChange, value } }) => (
            <TextInput
              label="Year"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="2024"
              error={errors.year?.message}
            />
          )}
        />
      </div>

      <div className="bg-gray-50 rounded-xl p-4">
        <div className="text-sm text-gray-500">Paper Code</div>
        <div className="font-mono text-lg font-semibold text-gray-900">{paperCode}</div>
        <div className="text-sm text-gray-600 mt-1">{paperDescription}</div>
      </div>

      <LoadingButton
        loading={false}
        type="submit"
        className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white border-none"
      >
        Next
      </LoadingButton>
    </form>
  );
};

const UploadStep: FC = () => {
  const { previousStep, nextStep, contextData, setContextData } = useStepperContext<typeof STEPS, UploadFormData>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const file = contextData?.file;

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setContextData((prev) => ({ ...prev, file: selectedFile }));
    }
  };

  const handleNext = () => {
    if (!file) return;
    nextStep();
  };

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Main PDF Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Question Paper</label>
        <div
          onClick={handleFileClick}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 cursor-pointer hover:border-gray-400 transition-colors"
        >
          <div className="flex flex-col items-center gap-3">
            <DocumentIcon className="w-10 h-10 text-gray-400" />
            {file ? (
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">{file.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
            ) : (
              <>
                <button type="button" className="text-[#C87941] hover:underline font-medium">
                  Click to upload
                </button>
                <span className="text-sm text-gray-500">PDF only</span>
              </>
            )}
          </div>
        </div>
      </div>

      <LoadingButton
        loading={false}
        type="button"
        className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white border-none"
        disabled={!file}
        onClick={handleNext}
      >
        Upload
      </LoadingButton>

      <button
        type="button"
        onClick={previousStep}
        className="w-full text-center text-gray-700 font-medium hover:text-gray-900"
      >
        Go back
      </button>
    </div>
  );
};

const ProcessingStep: FC = () => {
  const navigate = useNavigate();
  const { contextData, previousStep } = useStepperContext<typeof STEPS, UploadFormData>();
  const { mutateAsync: uploadPastPaper, isPending } = useUploadPastPaper();
  const hasTriggeredUpload = useRef(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    const doUpload = async () => {
      if (hasTriggeredUpload.current) return;

      if (!contextData?.file || !contextData.subject || !contextData.month || !contextData.year || !contextData.examBoard || !contextData.paperNumber) {
        previousStep();
        return;
      }

      hasTriggeredUpload.current = true;

      try {
        const result = await uploadPastPaper({
          file: contextData.file,
          subject: contextData.subject,
          examBoard: contextData.examBoard,
          paperNumber: contextData.paperNumber,
          month: contextData.month,
          year: contextData.year,
        });

        console.log('✅ Upload complete - navigating to edit page...');
        // Navigate to the edit page to review extracted questions
        navigate(Routes.ADMIN_EDIT_PAST_PAPER.replace(':paperId', result.pastPaperId));
      } catch (err) {
        console.error('Upload failed:', err);
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setUploadError(errorMessage);
      }
    };

    doUpload();
  }, [contextData, previousStep, uploadPastPaper, navigate]);

  if (uploadError) {
    // Parse validation errors from the message (they're joined by ", ")
    const errorMessage = uploadError;
    const isValidationError = errorMessage.includes('mismatch');
    const errors = isValidationError
      ? errorMessage.replace('Failed to process PDF: ', '').split(', ')
      : [errorMessage];

    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="text-red-500 text-center">
          <div className="text-lg font-medium">
            {isValidationError ? 'PDF Validation Failed' : 'Upload failed'}
          </div>
          <div className="text-sm mt-3 space-y-2">
            {errors.map((err, index) => (
              <div key={index} className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                {err}
              </div>
            ))}
          </div>
          {isValidationError && (
            <div className="text-gray-600 text-xs mt-4">
              Please check that you selected the correct paper details.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setUploadError(null);
            hasTriggeredUpload.current = false;
            previousStep();
          }}
          className="text-[#C87941] hover:underline font-medium"
        >
          Go back and try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="loading loading-spinner loading-lg text-[#C87941]"></div>
      <div className="text-[#C87941] font-medium">
        {isPending ? 'Processing...' : 'Uploading...'}
      </div>
    </div>
  );
};

const UploadPastPaperPage: FC = () => {
  const navigate = useNavigate();
  const { step } = useStepperContext<typeof STEPS, UploadFormData>();

  const handleBack = () => {
    navigate(Routes.ADMIN_PAST_PAPERS);
  };

  const getTitle = () => {
    if (step === 'processing') return 'Extraction in progress';
    return 'Upload a past paper PDF';
  };

  return (
    <div className="min-h-full">
      <PageHeader
        title={getTitle()}
        titleAlign="right"
        showBackButton
        onBackClick={handleBack}
        disableBackButton={step === 'processing'}
      />

      <div className="flex justify-center mt-8">
        <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200 p-8">
          <Stepper.Step step="details">
            <DetailsStep />
          </Stepper.Step>
          <Stepper.Step step="upload">
            <UploadStep />
          </Stepper.Step>
          <Stepper.Step step="processing">
            <ProcessingStep />
          </Stepper.Step>
        </div>
      </div>
    </div>
  );
};

const UploadPastPaperPageWrapper: FC = () => {
  return (
    <Stepper.Wrapper steps={STEPS} initialStep="details">
      <UploadPastPaperPage />
    </Stepper.Wrapper>
  );
};

export default UploadPastPaperPageWrapper;
